import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet } from "lucide-react";

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseLine = (l) => {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < l.length; i++) {
      const c = l[i];
      if (c === '"' && l[i + 1] === '"' && inQuotes) { cur += '"'; i++; }
      else if (c === '"') inQuotes = !inQuotes;
      else if (c === "," && !inQuotes) { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur);
    return out;
  };
  const headers = parseLine(lines[0]).map((h) => h.trim());
  const rows = lines.slice(1).filter(l => l.trim()).map((l) => {
    const cells = parseLine(l);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
    return obj;
  });
  return { headers, rows };
}

export default function BulkImportDialog({ open, onOpenChange, module, onDone }) {
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const allHeaders = module.fields.map((f) => f.name);

  const parse = () => {
    const p = parseCSV(csv);
    if (p.rows.length === 0) {
      toast.error("No data rows found. Include a header line, then data rows below.");
      return;
    }
    setPreview(p);
  };

  const submit = async () => {
    if (!preview || preview.rows.length === 0) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/bulk-import/${module.key}`, { records: preview.rows });
      toast.success(`Imported ${r.data.inserted} record${r.data.inserted === 1 ? "" : "s"}`);
      onDone?.();
      onOpenChange(false);
      setCsv("");
      setPreview(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const templateLine = allHeaders.join(",");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setCsv(""); setPreview(null); } onOpenChange(v); }}>
      <DialogContent className="max-w-3xl bg-white" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-sky-600" /> Bulk import — {module.label}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Paste CSV rows to bulk-create {module.label} records.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <div className="text-xs text-slate-600 mb-1">
              Paste CSV data. First row must be column headers. Available fields:
            </div>
            <pre className="text-[11px] mono text-slate-700 bg-slate-50 border border-slate-200 p-2 whitespace-pre-wrap break-all">{templateLine}</pre>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">CSV data</label>
            <Textarea
              rows={10}
              value={csv}
              onChange={(e) => { setCsv(e.target.value); setPreview(null); }}
              className="mono text-xs mt-1"
              placeholder={templateLine + "\nJane Doe,Support Worker,jane@example.com,..."}
              data-testid="bulk-import-csv"
            />
          </div>

          {preview && preview.rows.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                Preview — {preview.rows.length} row{preview.rows.length === 1 ? "" : "s"} (showing first 5)
              </div>
              <div className="max-h-48 overflow-auto border border-slate-200">
                <table className="data-table">
                  <thead>
                    <tr>{preview.headers.map((h) => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 5).map((r, i) => (
                      <tr key={i}>
                        {preview.headers.map((h) => (
                          <td key={h}>{r[h] || <span className="text-slate-300">—</span>}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="bulk-cancel"
          >Cancel</Button>
          {!preview ? (
            <Button
              onClick={parse} disabled={!csv.trim()}
              data-testid="bulk-parse"
              className="bg-slate-800 hover:bg-slate-900"
            >Parse CSV</Button>
          ) : (
            <Button
              onClick={submit} disabled={submitting || preview.rows.length === 0}
              className="bg-sky-700 hover:bg-sky-800"
              data-testid="bulk-submit"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {submitting ? "Importing…" : `Import ${preview.rows.length} row${preview.rows.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
